// ========================================
// Check-in Flow Component
// ========================================
// Multi-step feelings check-in process

import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCheckIns } from '../../hooks/useCheckIns';
import { CORE_EMOTIONS, LIFE_CATEGORIES, CoreEmotion } from '../../utils/emotionData';
import { EmotionsByCategory } from '../../types';
import './CheckinFlow.css';

const CheckinFlow = () => {
  const navigate = useNavigate();
  const { addCheckIn } = useCheckIns();

  const [currentStep, setCurrentStep] = useState(0);
  const [emotions, setEmotions] = useState<EmotionsByCategory>({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const currentCategory = LIFE_CATEGORIES[currentStep]!;

  const toggleEmotion = (emotion: CoreEmotion) => {
    const categoryKey = currentCategory.toLowerCase() as keyof EmotionsByCategory;
    const currentEmotions = emotions[categoryKey] || [];

    if (currentEmotions.includes(emotion)) {
      setEmotions({
        ...emotions,
        [categoryKey]: currentEmotions.filter((e) => e !== emotion),
      });
    } else {
      setEmotions({
        ...emotions,
        [categoryKey]: [...currentEmotions, emotion],
      });
    }
  };

  const isEmotionSelected = (emotion: CoreEmotion): boolean => {
    const categoryKey = currentCategory.toLowerCase() as keyof EmotionsByCategory;
    return emotions[categoryKey]?.includes(emotion) || false;
  };

  const handleNext = () => {
    if (currentStep < LIFE_CATEGORIES.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < LIFE_CATEGORIES.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await addCheckIn({
        emotions,
        notes: notes.trim() || undefined,
      });

      navigate('/dashboard');
    } catch (err) {
      console.error('Error submitting check-in:', err);
      setError('Failed to save check-in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isLastStep = currentStep === LIFE_CATEGORIES.length - 1;
  const totalEmotionsSelected = Object.values(emotions).reduce(
    (sum, cats) => sum + (cats?.length || 0),
    0
  );

  return (
    <div className="checkin-container">
      <div className="checkin-content">
        {/* Progress Bar */}
        <div className="checkin-progress">
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${((currentStep + 1) / LIFE_CATEGORIES.length) * 100}%` }}
            />
          </div>
          <p className="progress-text">
            Category {currentStep + 1} of {LIFE_CATEGORIES.length}
          </p>
        </div>

        {/* Check-in Card */}
        <div className="checkin-card glass-panel">
          <div className="checkin-header">
            <h1>{currentCategory}</h1>
            <p>Select the emotions you're experiencing in this area</p>
          </div>

          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          {/* Emotion Selector */}
          <div className="emotion-grid">
            {CORE_EMOTIONS.map((emotion) => (
              <button
                key={emotion}
                type="button"
                onClick={() => toggleEmotion(emotion)}
                className={`emotion-button ${isEmotionSelected(emotion) ? 'selected' : ''}`}
              >
                {emotion}
              </button>
            ))}
          </div>

          {/* Notes Section (on last step) */}
          {isLastStep && (
            <div className="notes-section">
              <label htmlFor="notes">Additional Notes (Optional)</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional thoughts or context about your check-in..."
                className="glass-input notes-textarea"
                rows={4}
              />
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="checkin-actions">
            <div className="action-left">
              {currentStep > 0 && (
                <button type="button" onClick={handlePrevious} className="btn-secondary">
                  Previous
                </button>
              )}
            </div>

            <div className="action-center">
              <button type="button" onClick={handleSkip} className="btn-ghost">
                Skip
              </button>
            </div>

            <div className="action-right">
              {!isLastStep ? (
                <button type="button" onClick={handleNext} className="glass-button">
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="glass-button"
                  disabled={submitting || totalEmotionsSelected === 0}
                >
                  {submitting ? 'Saving...' : 'Complete Check-in'}
                </button>
              )}
            </div>
          </div>

          <div className="summary-info">
            <p>{totalEmotionsSelected} emotions selected</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckinFlow;
